import { getResolution, latLngToCell } from 'h3-js'
import { NeoClusterPosition } from '../mapPosition'
import h3ResolutionResolve, { h3ResolutionResolveByZoom } from '~/prix/utils/h3ResolutionResolve'
import { SpaceGeoStandardType } from '~/packages/general/variable/spaceTime.types'
import { Variable, VariableUnitFormat } from '~/packages/general/variable/variables.typing'
import { StyleForLayer } from '~/components/mapContent/utils/styleForLayer'
import { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson'
import { MaskPolygonInDelimitation } from './maskPolygonInDelimitation'
import { formatAsBrNumber, truthy, Type } from '~/prix'
import centerOfMass from '@turf/center-of-mass'
import { ActionCall } from '~/prix/react/hooks/concurrentActions'
import { GenericObject } from '~/prix/types/type'
import { cellToGeoJsonPolygonRing } from '~/prix/utils/h3Coordinates'

/**
 *
 * @param positions
 * @param currentResolution
 * @returns Um objeto onde as chaves são os índices H3 e os valores são arrays de posições agrupadas por índice H3.
 */
export const calculateH3Groups = (positions: NeoClusterPosition[], currentResolution: number) => {
  return positions.reduce(
    (acc, point) => {
      if (point.status === 'hasChild' || point.status === 'hide') {
        return acc
      }

      if (currentResolution !== point.realResolution && currentResolution < point.realResolution) {
        const h3Index = latLngToCell(point.location.lat, point.location.lng, currentResolution)

        if (!acc[h3Index]) {
          acc[h3Index] = []
        }

        acc[h3Index].push(point)
      } else {
        const h3IndexKey = createH3GroupIndexKey(acc, point.h3)
        acc[h3IndexKey] = []
        acc[h3IndexKey].push(point)
      }

      return acc
    },

    {} as Record<string, typeof positions>,
  )
}

const createH3GroupIndexKey = (h3Groups: Record<string, NeoClusterPosition[]>, h3Index: string) => {
  let key = null
  let count = 0
  while (key === null) {
    const newKey = `${h3Index}-${count}`
    const indexFound = Object.entries(h3Groups).findIndex(([h3]) => h3 === newKey)

    if (indexFound < 0 || indexFound === undefined) {
      key = newKey
    }
    count++
  }

  return key
}

export const getInitialMaxResolution = (spaceDelimitationType: SpaceGeoStandardType | 'custom') =>
  h3ResolutionResolve(spaceDelimitationType)

export const getMaxResolution = (positions: NeoClusterPosition[]) => {
  return positions.reduce(
    (max, position) => (position.realResolution > max ? position.realResolution : max),
    0,
  )
}

/**
 *
 * @param variable
 * @returns Tipo de formatação da unidade (Porcentagem, contagem ou outro)
 */
export const getUnitFormat = (variable: Variable) =>
  variable.unitFormat ? variable.unitFormat : variable.highlight ? 'percentage' : 'count'

export const getRange = (
  h3Groups: Record<string, NeoClusterPosition[]>,
  unitFormat: VariableUnitFormat,
) => {
  const weights = Object.entries(h3Groups).map(([h3Index, groupedPoints]) => ({
    weight: groupedPoints.reduce((sum, item) => sum + (item.weight ? item.weight : 0), 0),
  }))

  return unitFormat === 'percentage'
    ? { min: 0, max: 100 }
    : weights.reduce(
        (acc, obj) => {
          return {
            min: Math.min(acc.min, obj.weight || 0),
            max: Math.max(acc.max, obj.weight || 0),
          }
        },
        { min: 0, max: 0 },
      )
}

export const cellToFeature = (
  h3: string,
  value: number,
  resolution: number,
  style?: StyleForLayer,
  customLegend?: string | null,
) => {
  const polygon = {
    type: 'Polygon',
    coordinates: [cellToGeoJsonPolygonRing(h3)],
  }

  const styles = style ? { ...style?.mapStyle({ value: value }) } : {}

  return {
    type: 'Feature',
    properties: {
      h3: h3,
      realResolution: resolution,
      value: value,
      customLegend,
      ...styles,
    },
    geometry: polygon,
  } as Feature<Polygon>
}

export const createHexagonFeature = (
  h3Groups: Record<string, NeoClusterPosition[]>,
  unitFormat: VariableUnitFormat,
  style: StyleForLayer,
  delimitation: Polygon | MultiPolygon | undefined,
  renderVisibleHexagons: (feature: Geometry | Feature<any, GenericObject>) => boolean | undefined,
  currentResolution: number,
  initialMaxResolution: number,
) => {
  return Object.entries(h3Groups)
    .map(([h3Index, groupedPoints]) => {
      let customLegend = null,
        finalValue = 0

      if (unitFormat === 'percentage') {
        const [numerator, denominator] = groupedPoints.reduce(
          (acc, item) => {
            acc[0] += item.numerator ?? 0

            acc[1] += item.denominator ?? 0

            return acc
          },

          [0, 0],
        )

        finalValue = denominator === 0 ? 100 : (numerator / denominator) * 100
        customLegend = `${formatAsBrNumber(numerator)} de ${formatAsBrNumber(denominator)}`
      } else {
        finalValue = groupedPoints.reduce((sum, item) => sum + (item.weight ? item.weight : 0), 0)
      }
      const feature = cellToFeature(
        h3Index.split('-')[0],
        finalValue,
        groupedPoints[0].realResolution,
        style,
        customLegend,
      )

      if (!renderVisibleHexagons(feature.geometry)) return null

      const newFeature =
        groupedPoints[0].parent && currentResolution > initialMaxResolution
          ? MaskPolygonInDelimitation(feature, groupedPoints[0].parent.geometry)[0]
          : feature

      return MaskPolygonInDelimitation(newFeature, delimitation, true)
    })
    .flat()
    .filter(truthy)
}

export const setItemsToPosition = (
  items: { value: number; h3: string }[],
  parent: Feature<Polygon | MultiPolygon>,
  h3Parent: string,
  positions: NeoClusterPosition[],
) => {
  items.forEach(({ value, h3 }) => {
    if (h3) {
      const resolution = getResolution(h3)

      // coloca mascara no h3
      const feature = cellToFeature(h3, value, resolution)
      const maskedFeature = MaskPolygonInDelimitation(feature, parent.geometry)[0]
      const newCoordinates = centerOfMass(maskedFeature.geometry)

      const newH3 = latLngToCell(
        newCoordinates.geometry.coordinates[1],
        newCoordinates.geometry.coordinates[0],
        resolution,
      )

      positions.push({
        weight: value,
        realResolution: resolution,
        h3: newH3,
        parent: parent,
        h3Parent: h3Parent,
        location: {
          lat: newCoordinates.geometry.coordinates[1],
          lng: newCoordinates.geometry.coordinates[0],
        },
        status: 'new',
      })
    }
  })

  return positions
}

export const callActionLoadHexagon = (
  feature: Feature<Polygon | MultiPolygon>,
  positions: NeoClusterPosition[],
  layerRequest: ActionCall<Type, Type>,
  hexagonFeatures: FeatureCollection<Polygon | MultiPolygon, GenericObject>,
  callAction: (input: any) => {
    abort: () => void
    resultPromise: Promise<any>
  },
  showH3Loading: (index: number) => void,
  updateH3Groups: () => Record<string, NeoClusterPosition[]>,
  zoomToHexagon: (feature: Feature) => void,
) => {
  const h3 = feature.properties!.h3
  const weight = feature.properties!.value
  const realResolution = feature.properties!.realResolution
  const polygon = feature
  polygon.properties!.hash = h3

  const indexHexagon = hexagonFeatures.features.findIndex(item => item.properties!.h3 === h3)

  layerRequest.input.space = {
    delimitationTitle: 'Agrupamento',
    delimitationType: 'custom',
    delimitationCustom: hexagonFeatures.features[indexHexagon],
    childrenType: 'item',
    childrenItemView: 'cluster',
    childrenTitle: 'Agrupamentos',
    childrenH3Resolution: realResolution + 1,
  }
  const { resultPromise } = callAction(layerRequest.input)

  showH3Loading(indexHexagon)

  resultPromise
    .then(value => {
      // remove feature clicado
      const indexPosition = positions.findIndex(
        item =>
          item.h3 === h3 &&
          item.weight === weight &&
          item.realResolution === realResolution &&
          item.status !== 'hide',
      )
      if (indexPosition && indexPosition >= 0) {
        //marca a posição clicada como hasChild
        positions[indexPosition].status = 'hasChild'

        // positions.splice(indexPosition, 1)
        removeNewPosition(positions, realResolution, h3)

        //adiciona novos carregados
        setItemsToPosition(value?.items, hexagonFeatures.features[indexHexagon], h3, positions)
      }

      updateH3Groups()
      zoomToHexagon(polygon)
    })
    .catch((reason: any) => {
      console.log(reason)
    })
    .finally(() => {})
}

export const removeNewPosition = (
  positions: NeoClusterPosition[],
  resolution: number,
  h3Parent: string,
) => {
  const h3ParentsToShow = [] as string[]
  positions.map((position, index) => {
    if (position.realResolution >= resolution + 1 && position.h3Parent !== h3Parent) {
      positions[index].status = 'hide'

      if (!h3ParentsToShow.includes(position.h3) && position.h3Parent)
        h3ParentsToShow.push(position.h3Parent)
    }
  })

  positions.map((position, index) => {
    if (
      position.h3 !== h3Parent &&
      h3ParentsToShow.includes(position.h3) &&
      position.realResolution <= resolution &&
      position.status === 'hasChild'
    ) {
      positions[index].status = undefined
    }
  })
}

export enum NeoClusterClickReturn {
  hexagonNotFound,
  queryFeature,
  maxZoom,
  zoomToHexagon,
}

export const handleClickHexagon = (
  currentZoom: number,
  maxResolution: number,
  h3: string,
  feature: Feature<Polygon | MultiPolygon, GenericObject>,
  hexagonFeatures: FeatureCollection<Polygon | MultiPolygon, GenericObject>,
): NeoClusterClickReturn => {
  const currentResolution = h3ResolutionResolveByZoom(currentZoom, undefined, maxResolution)
  //const maxResolution = h3ResolutionResolve(layers[layerIndex].space.delimitationType)
  const realResolution = feature.properties!.realResolution as number
  const isMaxZoom = currentZoom >= 18 || currentResolution >= realResolution
  const minResolutionToOpen = 11
  const minValueToOpen = 10

  const currentValue = feature.properties!.value as number

  const hexFeature = hexagonFeatures.features.find(f => f.properties!.h3 === h3)
  if (!hexFeature) {
    return NeoClusterClickReturn.hexagonNotFound
  }

  if (
    realResolution < minResolutionToOpen &&
    currentResolution >= realResolution &&
    currentValue > minValueToOpen
  ) {
    return NeoClusterClickReturn.queryFeature
  }

  if (isMaxZoom) {
    return NeoClusterClickReturn.maxZoom
  }

  return NeoClusterClickReturn.zoomToHexagon
}
